/**
 * Tasks for Canvas — assignment tracker for Canvas / Blackboard / D2L Brightspace
 *
 * Declares a single WAR group with no `use_dynamic_url` holding three static paths —
 * `icon-128.png`, `logo.png` and `options.html` — matched to all https origins. Each is
 * vendor-controlled and fetchable from any https page at a fixed URL.
 *
 * The icon is the cleanest signal: being an image it loads through `probeImage` with no
 * CORS grant, so it survives a strict page CSP.
 *
 * Manifest verified against version 1.6.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kabafodfnabokkkddjbnkgbcbmipdlmb';

export const tasksForCanvas: ExtensionDefinition = {
  id: ID,
  name: 'Tasks for Canvas – now supporting Blackboard, D2L Brightspace',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 800_000,
  notes:
    'Three static WAR on all https origins, no dynamic URL. The icon load is conclusive ' +
    'and needs no CORS grant.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load icon-128.png via <img> — a static icon exposed to every https origin; ' +
        'needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icon-128.png'),
    },
    {
      id: 'war-logo-image',
      kind: 'web-accessible-resource',
      description: 'Load logo.png via <img> — a second image path, corroborating the hit.',
      weight: 0.5,
      run: () => probeImage(ID, 'logo.png'),
    },
    {
      id: 'war-options-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch options.html — the options page, an independent non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'options.html'),
    },
  ],
};
